import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectAllSubmitterUserOptionsFromState,
  selectExpenseReportLoadingFromState as selectOutOfPocketReportsAllLoadingFromState,
  selectExpenseReportsFullPaginatedData as selectOutOfPocketReportsAllFullPaginatedData,
  selectExpenseReportsListModels as selectOutOfPocketReportsAllListModels,
  selectGettingOutOfPocketExpensesEnabledFromState,
  selectSubmitterUsers,
} from 'src/app/ApproverExpenseReports/redux/approverExpenseReports.selectors'
import {
  selectExpenseReportLoadingFromState as selectOutOfPocketReportsYourLoadingFromState,
  selectExpenseReportsFullPaginatedData as selectOutOfPocketReportsYourFullPaginatedData,
  selectExpenseReportsListModels as selectOutOfPocketReportsYourListModels,
} from 'src/app/SubmitterExpenseReports/redux/submitterExpenseReports.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ExpenseReportsBox, {
  ExpenseReportsAnalyticsEventProperties,
  ExpenseReportsBoxInfiniteScrollProps,
  ExpenseReportsFiltersBody,
} from 'src/components/ExpenseReportsBox/ExpenseReportsBox'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useBackgroundDispatch } from 'src/hooks'
import {
  ExpenseReportModel,
  ExpenseReportStatus,
} from 'src/models/expenseReport.model'
import {
  createSetIsDeleteExpenseReportsPopupOpen,
  createSetIsMarkExpenseReportsPaidPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  getApproverExpenseReports,
  getApproverExpenseReportsLength,
  getSubmitterExpenseReports,
  getSubmitterExpenseReportsLength,
  getSubmitterUsers,
} from 'src/redux/actions/data/expenses.actions'
import {
  selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  selectIsMarkExpenseReportsPaidPopupOpenFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOutOfPocketExpensesVisible,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

interface ApproverExpenseReportsProps {
  allExpenses?: boolean
  yourExpenses?: boolean
}

const ApproverExpenseReports: React.FC<ApproverExpenseReportsProps> = (
  props,
) => {
  const dispatch = useBackgroundDispatch()
  const expenseAllReportsFullPaginatedData = useSelector(
    selectOutOfPocketReportsAllFullPaginatedData,
  )
  const expenseAllReportsLoading = useSelector(
    selectOutOfPocketReportsAllLoadingFromState,
  )
  const expenseAllReports = useSelector(selectOutOfPocketReportsAllListModels)
  const expenseYourReportsFullPaginatedData = useSelector(
    selectOutOfPocketReportsYourFullPaginatedData,
  )
  const expenseYourReportsLoading = useSelector(
    selectOutOfPocketReportsYourLoadingFromState,
  )
  const expenseYourReports = useSelector(selectOutOfPocketReportsYourListModels)
  const gettingOutOfPocketExpensesEnabled = useSelector(
    selectGettingOutOfPocketExpensesEnabledFromState,
  )
  const outOfPocketExpensesEnabled = useSelector(
    selectOutOfPocketExpensesVisible,
  )
  const allMemberIdOptions = useSelector(selectAllSubmitterUserOptionsFromState)
  const membersFullPaginatedData = useSelector(selectSubmitterUsers)
  const isMarkExpenseReportsPaidPopupOpen = useSelector(
    selectIsMarkExpenseReportsPaidPopupOpenFromState,
  )
  const isDeleteExpenseReportPopupOpen = useSelector(
    selectIsCreateDeleteExpenseReportsPopupOpenFromState,
  )
  const [bulkSelected, setBulkSelected] = useState<boolean>(false)
  const [selectedExpenseReports, setSelectedExpenseReports] = useState<
    ExpenseReportModel[]
  >([])
  const user = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const hasApproverPermissions = user?.id === organization?.expenseApprover?.id
  const hasReadPermissions = user?.hasOrgBookkeeperPermissions
  const appName = useSelector(selectAppName)
  const canViewAllReports =
    (hasApproverPermissions || hasReadPermissions) && !!props.allExpenses
  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    ExpenseReportsAnalyticsEventProperties | undefined
  >(undefined)

  const showYourReports =
    (!canViewAllReports || !!props.yourExpenses) && !props.allExpenses

  const fetchExpenseAllReports = (
    filtersBody: ExpenseReportsFiltersBody,
    page: number | undefined,
  ): void => {
    dispatch(
      getApproverExpenseReports({
        page: page,
        ...filtersBody,
      }),
    )
  }

  const fetchExpenseYourReports = (
    filtersBody: ExpenseReportsFiltersBody,
    page: number | undefined,
  ): void => {
    dispatch(
      getSubmitterExpenseReports({
        page: page,
        ...filtersBody,
      }),
    )
  }

  const fetchSubmitterUsers = (): void => {
    dispatch(getSubmitterUsers())
  }

  const expenseAllReportsInfiniteScrollProps: ExpenseReportsBoxInfiniteScrollProps =
    {
      ...expenseAllReportsFullPaginatedData,
      fetch: fetchExpenseAllReports,
    }

  const expenseYourReportsInfiniteScrollProps: ExpenseReportsBoxInfiniteScrollProps =
    {
      ...expenseYourReportsFullPaginatedData,
      fetch: fetchExpenseYourReports,
    }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchSubmitterUsers,
    ...membersFullPaginatedData,
  }

  const handleExpenseReportsCheckboxClick = (
    option: ExpenseReportModel,
  ): void => {
    if (!selectedExpenseReports.includes(option)) {
      if (selectedExpenseReports.length + 1 === expenseAllReports.length) {
        setBulkSelected(true)
      }
      setSelectedExpenseReports((prev) => [...prev, option])
    } else {
      if (bulkSelected) {
        setBulkSelected(false)
      }
      setSelectedExpenseReports((prev) =>
        prev.filter((item) => item !== option),
      )
    }
  }

  const handleBulkExpenseCheckboxClick = (): void => {
    if (showYourReports && expenseYourReports !== undefined) {
      bulkSelected
        ? setSelectedExpenseReports([])
        : setSelectedExpenseReports(
            expenseYourReports
              .filter((item) => item.status === ExpenseReportStatus.Draft)
              .map((item) => item) || [],
          )
    } else if (expenseAllReports !== undefined) {
      bulkSelected
        ? setSelectedExpenseReports([])
        : setSelectedExpenseReports(
            expenseAllReports
              .filter((item) => item.status === ExpenseReportStatus.Reviewed)
              .map((item) => item) || [],
          )
    }
    setBulkSelected(!bulkSelected)
  }

  const handleReportBulkAction = (): void => {
    if (showYourReports) {
      handleDeleteReports()
    } else {
      handleReportPaidSubmit()
    }
  }

  const handleDeleteReports = (): void => {
    const expenseReportIds = selectedExpenseReports.map((item) => item.id)
    dispatch(createSetIsDeleteExpenseReportsPopupOpen(true, expenseReportIds))
  }

  const handleReportPaidSubmit = (): void => {
    const expenseReportIds = selectedExpenseReports.map((item) => item.id)
    const totalAmountApproved = selectedExpenseReports
      .map((expenseReport) => expenseReport.approvedAmountCents)
      .reduce((a, b) => a + b)
    dispatch(
      createSetIsMarkExpenseReportsPaidPopupOpen(
        true,
        expenseReportIds,
        totalAmountApproved,
      ),
    )
  }

  const handleCloseToolbar = (): void => {
    setBulkSelected(false)
    setSelectedExpenseReports([])
  }

  useEffect((): void => {
    setBulkSelected(false)
    setSelectedExpenseReports([])
  }, [showYourReports])

  useEffect((): void => {
    if (!isMarkExpenseReportsPaidPopupOpen) {
      setBulkSelected(false)
      setSelectedExpenseReports([])
    }
  }, [isMarkExpenseReportsPaidPopupOpen])

  useEffect((): void => {
    if (!isDeleteExpenseReportPopupOpen) {
      setBulkSelected(false)
      setSelectedExpenseReports([])
    }
  }, [isDeleteExpenseReportPopupOpen])

  useLayoutEffect((): void => {
    if (canViewAllReports) {
      dispatch(getApproverExpenseReportsLength())
    }
  }, [hasApproverPermissions, hasReadPermissions])

  useLayoutEffect((): void => {
    dispatch(getSubmitterExpenseReportsLength())
  }, [])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track(
        `${showYourReports ? 'your' : 'all'} expense reports filter applied`,
        {
          report_status: selectedFilters.report_status,
          report_submitted_by: selectedFilters.report_submitted_by,
        },
      )
    }
  }, [selectedFilters])

  return gettingOutOfPocketExpensesEnabled ? (
    <LoadingBackgroundPage />
  ) : outOfPocketExpensesEnabled ? (
    <>
      <Helmet>
        <title>{`Reimbursements | ${appName}`}</title>
        <meta
          name="description"
          content={`Reimbursement Reports page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex noPadding>
        <ExpenseReportsBox
          expenseReports={
            showYourReports || !!props.yourExpenses
              ? expenseYourReports
              : expenseAllReports
          }
          expenseReportsInfiniteScrollProps={
            showYourReports || !!props.yourExpenses
              ? expenseYourReportsInfiniteScrollProps
              : expenseAllReportsInfiniteScrollProps
          }
          bulkSelected={bulkSelected}
          selectedExpenseReports={selectedExpenseReports}
          handleExpenseReportsCheckboxClick={handleExpenseReportsCheckboxClick}
          handleBulkExpenseReportsCheckboxClick={handleBulkExpenseCheckboxClick}
          handleReportBulkAction={handleReportBulkAction}
          handleCloseToolbar={handleCloseToolbar}
          userOptions={allMemberIdOptions}
          userInfiniteScrollProps={userInfiniteScrollProps}
          allReports={!!props.allExpenses}
          hasApproverPermissions={hasApproverPermissions}
          loading={
            (showYourReports && expenseYourReportsLoading) ||
            (!showYourReports && expenseAllReportsLoading)
          }
          setSelectedFilters={setSelectedFilters}
          context={props.allExpenses ? 'approver' : 'submitter'}
        />
      </BackgroundPageContent>
    </>
  ) : (
    <NotFoundBackgroundPageContent />
  )
}

export default ApproverExpenseReports
